import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, faUsers, faClock, faPlay, 
  faCheckCircle, faChartLine, faDatabase, 
  faProjectDiagram, faLaptopCode, faCertificate, 
  faBriefcase, faCode
} from '@fortawesome/free-solid-svg-icons';
import './HeroSection.css';

const Looker = () => {
  const [showModal, setShowModal] = useState(false);
  const [formCourseName] = useState('Looker Certification');
  const [formData, setFormData] = useState({
    fullName: '',
    mobileNumber: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setShowModal(true);
  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData({ fullName: '', mobileNumber: '', email: '' });
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobileNumber') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const { fullName, mobileNumber, email } = formData;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!fullName || !emailRegex.test(email) || mobileNumber.length !== 10) {
      alert('Please fill in valid details.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://api.dikshasea.in/api/enrollments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, course: formCourseName }),
      });

      const result = await response.json();
      if (response.ok) {
        alert('Enrollment successful!');
        closeModal();
      } else {
        alert(result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('Submission error:', err);
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="hero-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="badge bg-primary bg-opacity-10 text-white mb-3">New Looker Certification</span>
              <h1 className="hero-title">
                Master <span className="hero-highlight">Looker</span>
              </h1>
              <p className="hero-subtitle">
                Become a Looker expert with our comprehensive certification program. 
                Gain hands-on experience with LookML, data modeling, and dashboard creation 
                to deliver powerful business intelligence solutions.
              </p>
              
              <div className="course-meta">
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faStar} />
                  </div>
                  <div>
                    <div className="fw-bold">4.7/5.0</div>
                    <small className="text-muted">(3,500 reviews)</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <div className="fw-bold">12,000+</div>
                    <small className="text-muted">Certified Professionals</small>
                  </div>
                </div>
                <div className="meta-item">
                  <div className="meta-icon">
                    <FontAwesomeIcon icon={faClock} />
                  </div>
                  <div>
                    <div className="fw-bold">65 hours</div>
                    <small className="text-muted">Hands-on Labs</small>
                  </div>
                </div>
              </div>
              
              <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
                Enroll Now
              </button>
            </div>
            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ratio ratio-16x9">
                <img 
                  src="subcourses-images/looker.jpg" 
                  alt="Looker Certification Course" 
                  className="img-fluid rounded-3 shadow"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="course-details">
          <div className="course-content">
            <section className="about-course">
              <h2 className="section-title">About This Certification</h2>
              <p>This comprehensive Looker certification program prepares you for business intelligence and data analyst roles specializing in Looker. You'll gain expertise in LookML development, data exploration, and embedded analytics used by data-driven organizations.</p>
              <p>Through real-world business scenarios and practical exercises, you'll build the skills needed to pass Looker certifications and implement professional BI solutions.</p>
            </section>
            
            <section className="curriculum">
              <h2 className="section-title">Certification Path</h2>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faDatabase} className="me-2" /> Module 1: Looker Fundamentals</h3>
                <p>Master core Looker concepts and interface</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Looker architecture</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Basic explores</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Dashboard creation</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Sharing & scheduling</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faCode} className="me-2" /> Module 2: LookML Development</h3>
                <p>Develop professional data models with LookML</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Dimensions & measures</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Joins & relationships</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Derived tables</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Performance optimization</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faChartLine} className="me-2" /> Module 3: Advanced Analytics</h3>
                <p>Master Looker's analytical capabilities</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Custom measures</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Table calculations</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Time-based analysis</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Advanced filters</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faProjectDiagram} className="me-2" /> Module 4: Dashboard Design</h3>
                <p>Create enterprise-grade dashboards</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Layout & formatting</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Interactive filters</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Custom visualizations</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Dashboard best practices</div>
                </div>
              </div>
              
              <div className="curriculum-item">
                <h3><FontAwesomeIcon icon={faBriefcase} className="me-2" /> Module 5: Looker Deployment</h3>
                <p>Implement Looker at organizational scale</p>
                <div className="topics">
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> User management</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Embedded analytics</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> API integration</div>
                  <div className="topic"><FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Content validation</div>
                </div>
              </div>
            </section>
          </div>  
          
          <div className="course-sidebar">
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faClock} />
              </div>
              <div className="info-content">
                <h4>Duration</h4>
                <p>8 Weeks (15-20 hrs/week)</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCertificate} />
              </div>
              <div className="info-content">
                <h4>Certifications</h4>
                <p>Looker Developer</p>
                <p>Looker Analyst</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faLaptopCode} />
              </div>
              <div className="info-content">
                <h4>Projects</h4>
                <p>4 Portfolio Projects</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className="info-content">
                <h4>Prerequisites</h4>
                <p>SQL Knowledge</p>
                <p>Basic BI Concepts</p>
              </div>
            </div>
            
            <div className="info-item">
              <div className="info-icon">
                <FontAwesomeIcon icon={faBriefcase} />
              </div>
              <div className="info-content">
                <h4>Career Support</h4>
                <p>Resume Preparation</p>
                <p>Interview Coaching</p>
              </div>
            </div>
            
            <button className="btn btn-primary btn-lg px-4" onClick={openModal}>
              Enroll Now
            </button>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div
          className="modal d-block fade show"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content p-4 d-flex flex-column flex-md-row">
              <div className="w-100 w-md-50 mb-3 mb-md-0">
                <img
                  src="/img/enroll.jpg"
                  alt="Enrollment"
                  className="img-fluid h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="w-100 w-md-50 ps-md-4">
                <h6 className="text-primary">Course:</h6>
                <h4 className="fw-bold">{formCourseName}</h4>
                <form onSubmit={handleSubmit} noValidate>
                  <input
                    className="form-control mb-3"
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    className="form-control mb-3"
                    type="tel"
                    name="mobileNumber"
                    placeholder="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    required
                    maxLength={10}
                  />
                  <input
                    className="form-control mb-3"
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="d-flex justify-content-end">
                    <button 
                      type="button" 
                      className="btn btn-outline-dark me-2" 
                      onClick={closeModal}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Looker;